
import KeenSlider from 'keen-slider';
import 'keen-slider/keen-slider.min.css';
import { GET_HERO_CONTENT } from '@/api/apolloHomePageQueries';

export default {
  name: 'HomeViewHeroBanner',
  props: {
    device: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isLoading: false,
      heroContent: [],
      sliders: new Map(),
      sliderStates: new Map(),
      timeouts: new Map(),
      focusedSliders: new Set(),
      isClient: false,
      hasMultipleSlides: false,
      isContentReady: false,
    };
  },
  computed: {
    currentHeroContent() {
      let content;
      if (this.device.isDesktop) content = this.heroContent.find((item) => item.isDesktop);
      else if (this.device.isTablet) content = this.heroContent.find((item) => item.isTablet);
      else content = this.heroContent.find((item) => item.isMobile);

      if (!content?.html) return content;

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content.html;

      if (!this.device.isDesktop) {
        const sliderDotsElement = tempDiv.querySelector('.slider-dots');
        const sliderElement = tempDiv.querySelector('.center_banner__slider');

        if (sliderDotsElement && sliderElement) {
          sliderDotsElement.remove();
          sliderElement.parentNode.insertBefore(sliderDotsElement, sliderElement.nextSibling);
        }
      }

      return {
        ...content,
        html: tempDiv.innerHTML,
      };
    },
  },
  async fetch() {
    await this.fetchData();
  },
  mounted() {
    this.isClient = true;
    this.$nextTick(() => {
      this.initializeSliders();
    });
  },
  watch: {
    currentHeroContent: {
      immediate: true,
      handler() {
        if (process.client) {
          if (this.currentHeroContent?.style) {
            this.updateStyles(this.currentHeroContent?.style);
            this.isContentReady = true;
          }
          this.$nextTick(() => {
            setTimeout(() => {
              this.destroyAllSliders();
              this.initializeSliders();
            }, 100);
          });
        }
      },
    },
  },
  beforeDestroy() {
    this.destroyAllSliders();
  },
  methods: {
    updateStyles(cssText) {
      if (!this.$isSpider) {
        const styleElement = document.createElement('style');
        styleElement.textContent = cssText;
        document.head.appendChild(styleElement);
      }
    },
    async fetchData() {
      try {
        this.isLoading = true;
        const { home } = await this.$graphql.default.request(GET_HERO_CONTENT);
        this.heroContent = home.heroContent.filter((item) => item.displayPage === 'home');
      } catch (error) {
        console.error('Error fetching hero content:', error);
      } finally {
        this.isLoading = false;
      }
    },
    initializeSliders() {
      if (!this.$refs.sliderHtmlWrapper) return;

      // Find all elements with isSlider class
      // Replace center_banner__many class with unique class where slider needed
      const sliderElements = this.$refs.sliderHtmlWrapper.querySelectorAll('.center_banner__many');
      sliderElements.forEach((sliderContainer, index) => {
        this.initializeSlider(sliderContainer, index);
      });
    },

    initializeSlider(sliderContainer, sliderId) {
      // Find the keen-slider element within the container
      const sliderElement = sliderContainer.querySelector('.keen-slider');
      if (!sliderElement) return;
      let hasMultiple = false;
      // Find all slides
      const slides = sliderElement.querySelectorAll('.keen-slider__slide');
      if (slides.length <= 1) return;
      if (slides.length > 1) {
        hasMultiple = true;
      }
      this.hasMultipleSlides = hasMultiple;
      // Initialize slider state
      this.sliderStates.set(sliderId, {
        current: 0,
        container: sliderContainer,
        element: sliderElement,
        slides,
        totalSlides: slides.length,
      });

      // Create KeenSlider instance
      const slider = new KeenSlider(
        sliderElement,
        {
          slides: slides.length,
          initial: 0,
          drag: true,
          loop: true,
          slidesPerView: 1,
          slideChanged: (s) => {
            const state = this.sliderStates.get(sliderId);
            if (state) {
              state.current = s.track.details.rel;
              this.updateDots(sliderId);
              this.playCurrentVideo(sliderId);
            }
          },
        },
        [
          (sliderEle) => this.createSliderPlugin(sliderEle, sliderId),
        ],
      );

      // Store slider instance
      this.sliders.set(sliderId, slider);

      // Setup dots functionality
      this.setupDots(sliderId);

      // Start auto-play
      this.startAutoPlay(sliderId);

      // Play initial video
      this.playCurrentVideo(sliderId);
    },

    createSliderPlugin(slider, sliderId) {
      const sliderElement = this.sliderStates.get(sliderId)?.element;
      if (!sliderElement) return;

      slider.on('animationEnded', () => this.handleSliderAnimationEnd(sliderId));
      slider.on('updated', () => this.startAutoPlay(sliderId));

      // Handle drag events - key fix here
      slider.on('dragStarted', () => {
        this.clearAutoPlay(sliderId);
      });

      slider.on('dragEnded', () => {
        // Add a small delay to ensure drag is fully completed
        setTimeout(() => {
          this.startAutoPlay(sliderId);
        }, 100);
      });

      // Tab navigation handling
      sliderElement.addEventListener('focusin', () => this.handleFocusIn(sliderId));
      sliderElement.addEventListener('focusout', () => this.handleFocusOut(sliderId));
      sliderElement.addEventListener('keydown', (event) => this.handleKeydown(event, sliderId));
    },
    setupDots(sliderId) {
      const state = this.sliderStates.get(sliderId);
      if (!state) return;

      const dotsContainer = state.container.querySelector('.slider-dots');
      if (!dotsContainer) return;

      // Clear existing dots
      dotsContainer.innerHTML = '';

      // Create dots
      for (let i = 0; i < state.totalSlides; i += 1) {
        const dot = document.createElement('button');
        dot.className = `dot ${i === 0 ? 'active' : ''}`;
        dot.addEventListener('click', () => this.moveToSlide(sliderId, i));
        dot.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') this.moveToSlide(sliderId, i);
        });
        dotsContainer.appendChild(dot);
      }
    },

    updateDots(sliderId) {
      const state = this.sliderStates.get(sliderId);
      if (!state) return;

      const dots = state.container.querySelectorAll('.dot');
      dots.forEach((dot, index) => {
        dot.classList.toggle('active', index === state.current);
      });
    },

    moveToSlide(sliderId, slideIndex) {
      const slider = this.sliders.get(sliderId);
      if (slider) {
        slider.moveToIdx(slideIndex);
      }
    },

    getCurrentSlideSpeed(sliderId) {
      const state = this.sliderStates.get(sliderId);
      if (!state) return 7000;

      const currentSlide = state.slides[state.current];
      const video = currentSlide?.querySelector('video');
      if (video) {
        const source = video.querySelector('source');
        if (source && source.src && source.src.includes('.mp4')) {
          return video.duration * 1000 || 7000;
        }
      }

      // Default speed or get from data attribute
      return parseInt(currentSlide?.dataset?.speed, 10) || 7000;
    },

    startAutoPlay(sliderId) {
      this.clearAutoPlay(sliderId);

      // Don't start auto-play if slider is focused
      if (this.focusedSliders.has(sliderId)) {
        return;
      }

      const speed = this.getCurrentSlideSpeed(sliderId);

      const timeout = setTimeout(() => {
        const slider = this.sliders.get(sliderId);
        if (slider && !this.focusedSliders.has(sliderId)) {
          slider.next();
        }
      }, speed);

      this.timeouts.set(sliderId, timeout);
    },

    clearAutoPlay(sliderId) {
      const timeout = this.timeouts.get(sliderId);
      if (timeout) {
        clearTimeout(timeout);
        this.timeouts.delete(sliderId);
      }
    },

    handleFocusIn(sliderId) {
      this.focusedSliders.add(sliderId);
      this.clearAutoPlay(sliderId);
    },

    handleFocusOut(sliderId) {
      // Add a small delay to handle rapid focus changes
      setTimeout(() => {
        this.focusedSliders.delete(sliderId);
        this.startAutoPlay(sliderId);
      }, 100);
    },

    handleKeydown(event, sliderId) {
      if (event.key === 'Tab') {
        const state = this.sliderStates.get(sliderId);
        if (!state) return;

        const nextIndex = (state.current + 1) % state.totalSlides;
        if (nextIndex === 0) return;

        event.preventDefault();
        const slider = this.sliders.get(sliderId);
        if (slider) {
          if (event.shiftKey) {
            slider.prev();
          } else {
            slider.next();
          }
        }
      }
    },

    handleSliderAnimationEnd(sliderId) {
      if (this.focusedSliders.has(sliderId)) {
        const state = this.sliderStates.get(sliderId);
        if (state) {
          const currentSlide = state.slides[state.current];
          const focusableElement = currentSlide?.querySelector('a, button, [tabindex]');
          focusableElement?.focus();
        }
      }
      // Restart auto-play after animation ends
      // Add a small delay to ensure all events are processed
      setTimeout(() => {
        this.startAutoPlay(sliderId);
      }, 50);
    },

    playCurrentVideo(sliderId) {
      const state = this.sliderStates.get(sliderId);
      if (!state) return;

      // Pause all videos in this slider
      state.slides.forEach((slide) => {
        const video = slide.querySelector('video');
        if (video) {
          video.pause();
          video.currentTime = 0;
        }
      });

      // Play current video
      const currentSlide = state.slides[state.current];
      const video = currentSlide?.querySelector('video');

      if (video) {
        const source = video.querySelector('source');
        if (source && source.src && source.src.includes('.mp4')) {
          video.currentTime = 0;

          // Preload the video if not already done
          if (video.readyState < 4) {
            video.load();
          }
          // Play immediately without delay for all devices
          video.play().catch(() => {
            // Retry after a short delay if initial play fails
            setTimeout(() => {
              // video.play().catch((err) => console.log('Video retry failed:', err));
            }, 100);
          });
        }
      }
    },

    destroyAllSliders() {
      // Clear all timeouts
      this.timeouts.forEach((timeout) => clearTimeout(timeout));
      this.timeouts.clear();

      // Destroy all sliders
      this.sliders.forEach((slider) => {
        try {
          slider.destroy();
        } catch (e) {
          console.warn('Error destroying slider:', e);
        }
      });
      this.sliders.clear();

      // Clear states
      this.sliderStates.clear();
      this.focusedSliders.clear();

      // Remove event listeners
      if (this.$refs.sliderHtmlWrapper) {
        const sliderElements = this.$refs.sliderHtmlWrapper.querySelectorAll('.keen-slider');
        sliderElements.forEach((element) => {
          // Clone and replace to remove all event listeners
          const newElement = element.cloneNode(true);
          element.parentNode?.replaceChild(newElement, element);
        });
      }
    },
  },
};
